import type { Dispatch } from 'umi';
import {
  checkNullString,
  getLastDateRange,
  getStaticDateText,
} from '@/pages/datalab/utils/utils';
import { DBColumnType } from '@/pages/datalab/components/SortableDrag/typeDefine';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { TDispatchFun } from '@/pages/datalab/components/SortableDrag/configration';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import { ChartType } from '@dbl/core/chart';
import { CHART_SETTINGS_NAMESPACE } from '@/pages/datalab/models/namespaces';

/**
 * 筛选区字段写回 model 的统一入口。
 * 从 FilterSortable 内联逻辑整体提取，避免拖拽 / 下拉 / 删除 / 批量取消等多处写回不一致。
 */

/** createFilterSortablePersist 入参：与 FilterSortable 写回 model 相关的上下文 */
export interface IFilterSortablePersistOptions {
  /** model 中筛选数组 key，通常为 columnFilters */
  sortableKey: string;
  chartType: string;
  /** 表格原始列 / 聚合模式等，影响对比时间联动条件 */
  mode: string;
  /** Trend 图删除日期筛选时联动清理 columnDimensions（与 close 删除逻辑对齐） */
  dimensions?: IColumnInfo[];
  dispatchFun: TDispatchFun;
  /** 对比时间需 dispatch chartSettings model，不能仅靠 dispatchFun('save') */
  dispatch: Dispatch;
}

/**
 * 筛选区「设置 / 删除」统一写回 API。
 * 各方法对应 FilterSortable 内不同 UI 入口，内部共享 transform 与副作用规则。
 */
export interface IFilterSortablePersist {
  /** 拖拽落库：过滤占位项 + 日期/普通字段 transform 后 save */
  saveList: (list: IColumnInfo[]) => void;
  /** 下拉/弹层改值：merge 后 save 或 updateStringFilter */
  changeItem: (
    currentList: IColumnInfo[],
    columnInfo: IColumnInfo,
    isUpdate?: boolean,
  ) => void;
  /** 删除字段：compareTimes + Trend 维度联动后 save */
  removeItems: (currentList: IColumnInfo[], removedItems: IColumnInfo[]) => void;
  /** 批量下拉关闭：写入 next 列表，取消勾选时走与 close 相同的删除副作用 */
  applyBatchChange: (
    nextFilters: IColumnInfo[],
    removedItems: IColumnInfo[],
  ) => void;
  /** 拖出筛选区时仅触发 compareTimes 联动（列表变更由 setList 负责） */
  notifyCompareTimeIfNeeded: (fieldInfo?: IColumnInfo) => void;
}

/** 单个筛选字段 transform（拖拽落库 / 批量新增共用） */
export const normalizeFilterField = (item: IColumnInfo): IColumnInfo => {
  if (item.column_type === 'date') {
    const newFilter: IColumnInfo = {
      operation: DBColumnType.getFilterOperation(item),
      lastCount: item.column_granularity === 'day' ? 7 : 1,
      dateType: item.column_granularity?.replace(/\d/, '') || 'day',
      ...item,
    };
    newFilter.column_value = DBColumnType.getFilterValues(newFilter);
    const lastCount = newFilter.lastCount ?? (item.column_granularity === 'day' ? 7 : 1);
    const dateTypeValue = newFilter.dateType || 'day';
    newFilter.column_text = newFilter?.column_text?.includes('~')
      ? newFilter.column_text
      : getStaticDateText(
        getLastDateRange(lastCount, dateTypeValue, newFilter.from),
        dateTypeValue,
      );
    return newFilter;
  }
  return {
    operation: DBColumnType.getFilterOperation(item),
    column_value: DBColumnType.getFilterValues(item),
    ...item,
  };
};

export const createFilterSortablePersist = (
  options: IFilterSortablePersistOptions,
): IFilterSortablePersist => {
  const { sortableKey, chartType, mode, dimensions, dispatchFun, dispatch } =
    options;

  /**
   * 表格聚合模式下删除「对比时间」筛选器时，需同步清空 chartSettings 中的 compareTimes，
   * 否则列排序/对比列展示仍保留旧配置。
   */
  const notifyCompareTimeIfNeeded = (fieldInfo?: IColumnInfo): void => {
    const compareTimes = (fieldInfo as IColumnInfo & { compareTimes?: unknown[]; })
      ?.compareTimes;
    if (
      chartType !== ChartType.Table ||
      mode !== ChartCalculateMode.Aggregate ||
      !(compareTimes?.length ?? 0)
    ) {
      return;
    }
    dispatch({
      type: `${CHART_SETTINGS_NAMESPACE}/watchCompareTime`,
      payload: {
        compareTimes: [],
        fieldInfo,
      },
    });
  };

  /** 拖拽落库：过滤占位项后对每条字段做 normalizeFilterField */
  const normalizeFilterList = (list: IColumnInfo[]): IColumnInfo[] =>
    list
      .filter(item => !checkNullString(item.id))
      .map(item => normalizeFilterField(item));

  /**
   * 组装 save payload；Trend 图删除日期类筛选时同步剔除 x 轴维度中同 id 字段。
   * 注意：维度联动仅比对 item.id（与历史 close 删除逻辑保持一致）。
   */
  const buildSavePayload = (
    nextFilters: IColumnInfo[],
    removedItems: IColumnInfo[] = [],
  ): Record<string, IColumnInfo[]> => {
    const payload: Record<string, IColumnInfo[]> = {
      [sortableKey]: nextFilters,
    };
    if (chartType === ChartType.Trend && removedItems.length > 0) {
      const removedIds = new Set(removedItems.map(item => item.id));
      payload.columnDimensions = (dimensions ?? []).filter(
        tagsItem =>
          !checkNullString(tagsItem.id) && !removedIds.has(tagsItem.id),
      );
    }
    return payload;
  };

  /** 拖拽 setList 入口：必须经过 normalize，不可直接写原始 list */
  const saveList = (list: IColumnInfo[]): void => {
    dispatchFun('save', { [sortableKey]: normalizeFilterList(list) });
  };

  /**
   * 下拉/弹层改值入口。
   * - 用 id + table_code 定位，避免同表同名字段误合并
   * - isUpdate 时走 updateStringFilter，仅刷新字符串筛选选项而不触发完整 save 链
   * - 不做 normalize：改值场景字段已具备 operation/column_value，避免覆盖用户配置
   */
  const changeItem = (
    currentList: IColumnInfo[],
    columnInfo: IColumnInfo,
    isUpdate?: boolean,
  ): void => {
    const index = currentList.findIndex(
      item =>
        item.id === columnInfo.id && item.table_code === columnInfo.table_code,
    );
    if (index < 0) {
      return;
    }
    const nextList = currentList.map((item, itemIndex) =>
      itemIndex === index ? { ...item, ...columnInfo } : item,
    );
    const nextFilters = nextList.filter(item => !checkNullString(item.id));
    if (isUpdate) {
      dispatchFun('updateStringFilter', { columnFilters: nextFilters });
      return;
    }
    dispatchFun('save', { [sortableKey]: nextFilters });
  };

  /**
   * 批量下拉 / 含新增时的写回：有 removed 则 compareTimes + Trend 联动，否则普通 save。
   */
  const applyBatchChange = (
    nextFilters: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): void => {
    if (removedItems.length > 0) {
      removedItems.forEach(item => notifyCompareTimeIfNeeded(item));
      dispatchFun('save', buildSavePayload(nextFilters, removedItems));
      return;
    }
    dispatchFun('save', { [sortableKey]: nextFilters });
  };

  /**
   * close 删除入口：从 currentList 剔除 removed 后委托 applyBatchChange。
   */
  const removeItems = (
    currentList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): void => {
    if (!removedItems.length) {
      return;
    }
    const removedKeySet = new Set(
      removedItems.map(item => `${item.id}_${item.table_code}`),
    );
    const nextFilters = currentList.filter(item => {
      if (checkNullString(item.id)) {
        return false;
      }
      return !removedKeySet.has(`${item.id}_${item.table_code}`);
    });
    applyBatchChange(nextFilters, removedItems);
  };

  return {
    saveList,
    changeItem,
    removeItems,
    applyBatchChange,
    notifyCompareTimeIfNeeded,
  };
};

